import { gql, useMutation } from "@apollo/client";
import { datadogLogs } from "@datadog/browser-logs";
import type { ICustomerAddressDeleteMutation } from "@repo/shopify/src/queries/customer-gql";
import { customerAddressDeleteMutation } from "@repo/shopify/src/queries/customer-gql";
import { extractId, matchExtractedId } from "@repo/shopify/src/utils";
import { formatOrderAddress } from "@repo/shopify/src/utils/address";
import type { MailingAddress } from "@shopify/hydrogen-react/storefront-api-types";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import Button from "@components/atoms/Button";
import GreyBox from "@components/atoms/GreyBox";
import Icon from "@components/atoms/Icon";
import TitleWrap from "@components/atoms/TitleWrap";
import AddressItem from "@components/molecules/AddressItem";

import Radio from "@atoms/Input/Radio";

import AddressForm from "@organisms/AddressForm";
import { useCheckoutContext } from "@organisms/Checkout/CheckoutContext";

import useLayout from "@hooks/reducerHooks/useLayout";
import useUser from "@hooks/reducerHooks/useUser";

import { IconName } from "@const/enum";

function ShippingAddress() {
  const { customer, accessToken, fetchUser } = useUser();
  const { openModal, closeModal } = useLayout();
  const { t } = useTranslation();

  const { setAddresses, selectedAddress, selectedAddressId, setSelectedAddressId } = useCheckoutContext();

  /** 배송지 목록 노출 여부 */
  const [showList, setShowList] = useState(false);
  /** 배송지 추가/수정 Form 노출 여부 */
  const [showForm, setShowForm] = useState(false);
  /** 수정할 배송지 */
  const [editableAddress, setEditableAddress] = useState<MailingAddress | null>();

  const addressList = useMemo(
    () =>
      customer.addresses.edges.length
        ? [
            customer.defaultAddress,
            ...customer.addresses.edges
              .filter(({ node }) => node.id !== customer.defaultAddress?.id)
              .map(({ node }) => node)
              .reverse(),
          ]
        : [],
    [customer.addresses],
  );

  useEffect(() => {
    parseAddress();
  }, [customer.addresses, customer.defaultAddress, addressList]);

  // customer의 addersses + defaultAddress를 가져온 다음에 처리해줘야 하는 것들
  const parseAddress = () => {
    if (!addressList.length) {
      setShowList(false);
      setShowForm(true);
      return;
    }

    setSelectedAddressId(selectedAddressId ?? customer.defaultAddress?.id ?? addressList[0]?.id);
    setAddresses(
      addressList.length
        ? addressList
            .filter((address) => matchExtractedId(address?.id || "", customer.defaultAddress?.id || ""))
            .concat(
              addressList.filter((address) => !matchExtractedId(address?.id || "", customer.defaultAddress?.id || "")),
            )
        : [],
    );
  };

  /** 배송지 삭제 */
  const [deleteCustomerAddress] = useMutation<ICustomerAddressDeleteMutation>(
    gql(customerAddressDeleteMutation.query),
    {
      onCompleted: () => {
        setSelectedAddressId();
        void fetchUser({ variables: { customerAccessToken: accessToken } });
      },
      onError: (error) => {
        datadogLogs.logger.error("[SHOPIFY][CustomerAddressDelete] Failed to mutation", error);
        throw new Error(`[SHOPIFY][CustomerAddressDelete] Failed to mutation : ${JSON.stringify(error)}`);
      },
    },
  );

  const handleAddressDeleteModal = (id: string) => {
    openModal({
      title: "Delete Address",
      description: t("account.shipping.delete_ask"),
      confirmButton: {
        label: "Okay",
        onClick: () => {
          void deleteCustomerAddress({ variables: { id, customerAccessToken: accessToken } });
          closeModal();
        },
      },
      cancelButton: {
        label: "Close",
      },
    });
  };

  const address = formatOrderAddress({ address: selectedAddress, lang: "ko" });

  return (
    <div>
      <TitleWrap title="Shipping Address" />
      <GreyBox className="mt-2.5">
        {/** 배송지 정보 Item */}
        {!showList && !showForm && Boolean(selectedAddress) && (
          <AddressItem {...address}>
            <Button
              className="h-8 w-10"
              name={t("account.shipping.edit")}
              onClick={() => {
                setShowList(false);
                setShowForm(true);
                setEditableAddress(selectedAddress);
              }}
              styleType="secondary"
              variant="containedIcon"
            >
              <Icon height="18px" name={IconName.Edit_100} width="18px" />
            </Button>
          </AddressItem>
        )}

        {/** 배송지 추가/수정 Form */}
        {showForm ? (
          <div>
            <AddressForm
              address={{
                ...editableAddress,
                id: editableAddress?.id ?? "",
                formatted: editableAddress?.formatted ?? [],
                isDefaultAddress: editableAddress
                  ? matchExtractedId(customer.defaultAddress?.id || "", editableAddress.id || "")
                  : false,
              }}
              onClickCancel={
                addressList.length
                  ? () => {
                      setShowForm(false);
                      setEditableAddress(undefined);
                    }
                  : undefined
              }
              onClickSubmit={() => {
                setShowForm(false);
                setEditableAddress(undefined);
              }}
            />
          </div>
        ) : null}
      </GreyBox>
    </div>
  );
}

export default ShippingAddress;
